import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import LaunchIcon from "@mui/icons-material/Launch";
import GitHubIcon from "@mui/icons-material/GitHub";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ProjectVisual from "./ProjectVisual";

const tagColors = {
  Web3: { bg: "rgba(147, 51, 234, 0.15)", color: "#a855f7" },
  "Full-Stack": { bg: "rgba(0, 127, 255, 0.15)", color: "#007FFF" },
  MUI: { bg: "rgba(0, 212, 170, 0.15)", color: "#00D4AA" },
  DeFi: { bg: "rgba(255, 159, 64, 0.15)", color: "#ff9f40" },
  SaaS: { bg: "rgba(236, 72, 153, 0.15)", color: "#ec4899" },
  "Real-time": { bg: "rgba(34, 197, 94, 0.15)", color: "#22c55e" },
  FinTech: { bg: "rgba(59, 130, 246, 0.15)", color: "#3b82f6" },
  EdTech: { bg: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" },
  Automation: { bg: "rgba(168, 85, 247, 0.15)", color: "#a855f7" },
  Enterprise: { bg: "rgba(99, 102, 241, 0.15)", color: "#6366f1" },
  "E-commerce": { bg: "rgba(236, 72, 153, 0.15)", color: "#ec4899" },
  Stripe: { bg: "rgba(99, 91, 255, 0.15)", color: "#635bff" },
  "Oracle Fusion": { bg: "rgba(0, 127, 255, 0.15)", color: "#4DA3FF" },
  Invoicing: { bg: "rgba(0, 212, 170, 0.15)", color: "#00D4AA" },
  Accounting: { bg: "rgba(34, 197, 94, 0.15)", color: "#22c55e" },
  GL: { bg: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" },
  Reports: { bg: "rgba(59, 130, 246, 0.15)", color: "#3b82f6" },
  Dashboard: { bg: "rgba(236, 72, 153, 0.15)", color: "#ec4899" },
  React: { bg: "rgba(0, 127, 255, 0.15)", color: "#4DA3FF" },
  Wallets: { bg: "rgba(147, 51, 234, 0.15)", color: "#a855f7" },
  DApp: { bg: "rgba(147, 51, 234, 0.15)", color: "#a855f7" },
};

function ProjectCard({ project, featured = false }) {
  const getTagStyle = (tag) => {
    return tagColors[tag] || { bg: "rgba(0, 127, 255, 0.15)", color: "#007FFF" };
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        "&::before": featured
          ? {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "linear-gradient(90deg, #007FFF 0%, #00D4AA 100%)",
          }
          : {},
      }}
    >
      {/* Image */}
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <ProjectVisual
          project={project}
          height={featured ? 220 : 180}
          variant="card"
        />
        {/* Year badge */}
        {/* <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(4px)",
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            {project.year}
          </Typography>
        </Box> */}
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 2.5 }}>
        {/* Tags */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {project.tags?.slice(0, 3).map((tag) => {
            const style = getTagStyle(tag);
            return (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  backgroundColor: style.bg,
                  color: style.color,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 24,
                }}
              />
            );
          })}
        </Box>

        {/* Title */}
        <Typography
          variant={featured ? "h5" : "h6"}
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            transition: "color 0.2s ease",
            "&:hover": {
              color: "primary.main",
            },
          }}
        >
          {project.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: featured ? 3 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.6,
          }}
        >
          {project.description}
        </Typography>

        {/* Technologies */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {project.technologies?.slice(0, 4).map((tech) => (
            <Typography
              key={tech}
              variant="caption"
              sx={{
                color: "text.secondary",
                backgroundColor: (theme) => alpha(theme.palette.divider, 0.1),
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: "0.65rem",
              }}
            >
              {tech}
            </Typography>
          ))}
          {project.technologies?.length > 4 && (
            <Typography
              variant="caption"
              sx={{
                color: "primary.main",
                fontSize: "0.65rem",
              }}
            >
              +{project.technologies.length - 4} more
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button
          component={Link}
          to={`/project/${project.id}`}
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          sx={{ mr: "auto" }}
        >
          View Details
        </Button>
        {project.demoLink && (
          <IconButton
            component="a"
            href={project.demoLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open live demo for ${project.title}`}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": { color: "primary.main" },
            }}
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
        )}
        {project.ghLink && (
          <IconButton
            component="a"
            href={project.ghLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open source code for ${project.title}`}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
          >
            <GitHubIcon fontSize="small" />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
}

export default ProjectCard;
